import React from "react";
import { Box, Skeleton } from "@mui/material";
import { Droppable } from "@hello-pangea/dnd";
import { dt } from "../../../../shared/themes/redesignTokens";
import { WorkOrder, WorkOrderStatusCode } from "../../models/models";
import BoardTaskCard from "./BoardTaskCard";
import QuickCreateCard from "./QuickCreateCard";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface BoardColumnProps {
  columnKey: string;
  title: string;
  count: number;
  color: string;
  tintColor: string;
  workOrders: WorkOrder[];
  onCardClick: (id: string) => void;
  highlightedIds?: Set<string>;
  isDragEnabled: boolean;
  showQuickCreate?: boolean;
  onQuickCreated?: () => void;
  isLoading?: boolean;
  columnIndex?: number;
}

const COMPLETED_STATUSES = [WorkOrderStatusCode.Completed, WorkOrderStatusCode.Verified];

const SkeletonCard = () => (
  <Box
    sx={{
      display: "flex",
      borderRadius: "10px",
      overflow: "hidden",
      backgroundColor: "#FFFFFF",
    }}
  >
    {/* Skeleton stripe */}
    <Skeleton variant="rectangular" width={4} sx={{ flexShrink: 0, borderRadius: 0 }} />
    {/* Skeleton content */}
    <Box sx={{ flex: 1, padding: "14px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Skeleton variant="rounded" width={86} height={14} sx={{ borderRadius: "4px" }} />
        <Skeleton variant="rounded" width={50} height={16} sx={{ borderRadius: "4px" }} />
      </Box>
      <Skeleton variant="rounded" width="90%" height={14} sx={{ borderRadius: "4px" }} />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Skeleton variant="rounded" width="70%" height={12} sx={{ borderRadius: "4px" }} />
        <Skeleton variant="rounded" width="60%" height={12} sx={{ borderRadius: "4px" }} />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Skeleton variant="circular" width={22} height={22} />
          <Skeleton variant="rounded" width={50} height={12} sx={{ borderRadius: "4px" }} />
        </Box>
        <Skeleton variant="rounded" width={36} height={12} sx={{ borderRadius: "4px" }} />
      </Box>
    </Box>
  </Box>
);

const BoardColumn = ({
  columnKey,
  title,
  count,
  color,
  tintColor,
  workOrders,
  onCardClick,
  highlightedIds,
  isDragEnabled,
  showQuickCreate,
  onQuickCreated,
  isLoading,
  columnIndex = 0,
}: BoardColumnProps) => {
  const { translate } = useTranslation("operations");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: { xs: "100%", sm: "220px", lg: "260px" },
        maxWidth: { lg: "320px" },
        flex: "1 0 0",
        flexShrink: 0,
        alignSelf: "stretch",
        animation: `fadeSlideUp 0.4s ease ${columnIndex * 0.06}s backwards`,
      }}
    >
      {/* Column header: dot + name + count (spec 4.2) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",             /* spec: dot-to-label 8px, label-to-count 8px */
          paddingBottom: "12px",  /* spec: header bottom padding 12px */
          paddingLeft: "4px",
          paddingRight: "4px",
        }}
      >
        {/* Colored dot */}
        <Box
          sx={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        {/* Column name */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 600,
            color: "#0C1117",  /* spec: text-primary */
            lineHeight: "16px",
          }}
        >
          {title}
        </Box>
        {/* Count */}
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "11px",
            fontWeight: 500,
            color: "#8C9099",  /* spec: text-muted */
            lineHeight: "14px",
          }}
        >
          {count}
        </Box>
      </Box>

      {/* Scrollable card area wrapped in Droppable */}
      <Droppable
        droppableId={columnKey}
        type="CARD"
      >
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",            /* spec: card gap 12px */
              minHeight: "80px",
              flexGrow: 1,
              flexBasis: "auto",
              transition: "background-color 0.2s ease, border 0.2s ease",
              backgroundColor: snapshot.isDraggingOver ? `${color}12` : "transparent",
              border: snapshot.isDraggingOver
                ? `2px dashed ${color}50`
                : "2px solid transparent",
              borderRadius: "8px",
            }}
          >
            {isLoading ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
                {columnIndex < 2 && <SkeletonCard />}
              </>
            ) : workOrders.length === 0 && !snapshot.isDraggingOver && !showQuickCreate ? (
              <Box
                sx={{
                  textAlign: "center",
                  padding: "20px 12px",
                  fontFamily: dt.font,
                  fontSize: "12px",
                  color: "#8C9099",
                  fontStyle: "italic",
                }}
              >
                {translate("board.emptyColumn")}
              </Box>
            ) : (
              workOrders.map((wo, index) => (
                <BoardTaskCard
                  key={wo.id}
                  workOrder={wo}
                  onClick={onCardClick}
                  highlighted={highlightedIds?.has(wo.id)}
                  index={index}
                  isDragEnabled={isDragEnabled}
                  isCompleted={COMPLETED_STATUSES.includes(wo.workOrderStatusId)}
                />
              ))
            )}
            {provided.placeholder}

            {/* Quick-create card (Backlog column only) */}
            {showQuickCreate && onQuickCreated && (
              <QuickCreateCard onCreated={onQuickCreated} />
            )}
          </Box>
        )}
      </Droppable>
    </Box>
  );
};

export default BoardColumn;
